import * as React from "react"
import { useState, useCallback, useEffect } from "react"
import { DrawerSeeker } from "@ui-seeker/core"
import { Grid, TextField, Typography, ToggleButton, ToggleButtonGroup, Box, FormControlLabel, Switch, MenuItem, Accordion, AccordionSummary, AccordionDetails, Tooltip, InputAdornment } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import { isCreateScheduleDrawerOpen, scheduleFormData, resetScheduleFormData } from "../@signals"
import { createBatchFormData } from "../../Batches/@signals"
import { useScheduleMutations } from "../@hooks"
import ScheduleExecutionFields from "../ScheduleExecutionFields"
import { SitesAutocomplete, BrandsAutocomplete, CustomerAutocomplete } from "../../Batches/CreateBatchDrawer/@components"
import { buildSchedulePayload, isScheduleFormValid, BATCH_TYPE_OPTIONS, FREQUENCY_OPTIONS, DATE_RANGE_OPTIONS, CUSTOMER_REQUIRED_TYPES } from "../scheduleForm"


export default function CreateScheduleDrawer() {
    const { createSchedule, isSubmitting } = useScheduleMutations()
    const [isValid, setIsValid] = useState(false)

    // Validate form
    useEffect(() => {
        setIsValid(isScheduleFormValid(scheduleFormData.value, createBatchFormData.value))
    }, [scheduleFormData.value, createBatchFormData.value])

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            isCreateScheduleDrawerOpen.value = false
            resetScheduleFormData()
        }
    }, [isSubmitting])

    const handleCreate = useCallback(async () => {
        const data = scheduleFormData.value

        // The DAY only. It used to be sent as '...T00:00:00Z', which pinned the run to
        // midnight UTC and left the real hour to whatever the cron fired at; the back
        // now anchors a date-only value at the schedule's execution_time.
        const scheduleData = {
            ...buildSchedulePayload(data, createBatchFormData.value),
            next_execution: data.next_execution || new Date().toISOString().split('T')[0]
        }

        try {
            await createSchedule(scheduleData)
            handleClose()
        } catch (err) {
            // Error handled by hook
        }
    }, [createSchedule, handleClose])

    const batchType = scheduleFormData.value.batch_type

    // Initialize createBatchFormData when drawer opens
    useEffect(() => {
        if (isCreateScheduleDrawerOpen.value) {
            const today = new Date()
            const sevenDaysAgo = new Date(today)
            sevenDaysAgo.setDate(today.getDate() - 7)
            const formatDate = (date) => date.toISOString().split('T')[0]

            // Sync createBatchFormData with scheduleFormData for autocompletes
            createBatchFormData.value = {
                ...createBatchFormData.value,
                batchType: batchType,
                sites: scheduleFormData.value.config.sites || [],
                brands: scheduleFormData.value.config.brands || [],
                dateStart: batchType === 'raw2sku' ? formatDate(sevenDaysAgo) : null,
                dateEnd: batchType === 'raw2sku' ? formatDate(today) : null
            }
        }
    }, [isCreateScheduleDrawerOpen.value, batchType])

    return (
        <DrawerSeeker
            title="Create Batch Schedule"
            isOpen={isCreateScheduleDrawerOpen.value}
            controller={handleClose}
            successProps={{
                onClick: handleCreate,
                disabled: !isValid || isSubmitting,
                children: isSubmitting ? "Creating..." : "Create Schedule"
            }}
            cancelProps={{
                disabled: isSubmitting
            }}
            width="50%"
        >
            <Grid container spacing={3}>
                {/* Schedule Name */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        required
                        label="Schedule Name"
                        placeholder="e.g., Nike Monthly Raw2SKU"
                        value={scheduleFormData.value.schedule_name}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            schedule_name: e.target.value
                        }}
                        disabled={isSubmitting}
                    />
                </Grid>

                {/* Batch Type */}
                <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Batch Type
                        </Typography>
                        <ToggleButtonGroup
                            value={batchType}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    scheduleFormData.value = {
                                        ...scheduleFormData.value,
                                        batch_type: newValue
                                    }
                                }
                            }}
                            disabled={isSubmitting}
                            fullWidth
                            size="small"
                        >
                            {BATCH_TYPE_OPTIONS.map(opt => (
                                <ToggleButton key={opt.value} value={opt.value}>
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Grid>

                {/* Customer — required for the directions that are scoped by one */}
                {CUSTOMER_REQUIRED_TYPES.includes(batchType) && (
                    <Grid item xs={12}>
                        <CustomerAutocomplete
                            disabled={isSubmitting}
                            clientType={batchType === 'raw2raw' ? 'retailer' : undefined}
                        />
                    </Grid>
                )}

                {/* Frequency */}
                <Grid item xs={scheduleFormData.value.frequency === 'custom' ? 8 : 12}>
                    <TextField
                        select
                        fullWidth
                        required
                        label="Frequency"
                        helperText="Batches will be named: [Schedule Name] - YYYY-MM-DD"
                        value={scheduleFormData.value.frequency}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            frequency: e.target.value
                        }}
                        disabled={isSubmitting}
                    >
                        {FREQUENCY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {scheduleFormData.value.frequency === 'custom' && (
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Every N days"
                            value={scheduleFormData.value.every_n_days ?? ''}
                            onChange={e => scheduleFormData.value = {
                                ...scheduleFormData.value,
                                every_n_days: e.target.value === '' ? null : e.target.value
                            }}
                            disabled={isSubmitting}
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </Grid>
                )}

                {/* Next Execution */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Next Execution Date"
                        helperText="First day the schedule runs"
                        value={scheduleFormData.value.next_execution || ''}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            next_execution: e.target.value
                        }}
                        disabled={isSubmitting}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </Grid>

                {/* Execution time — set it to the hour the client's catalog scraper is
                    done, so the batch reads a finished extraction and not a running one */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="time"
                        label="Execution Time"
                        helperText="Office time (Europe/Madrid). Empty = 08:00"
                        value={scheduleFormData.value.execution_time || ''}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            execution_time: e.target.value
                        }}
                        disabled={isSubmitting}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </Grid>

                {/* Active Toggle */}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={scheduleFormData.value.is_active}
                                onChange={e => scheduleFormData.value = {
                                    ...scheduleFormData.value,
                                    is_active: e.target.checked
                                }}
                                disabled={isSubmitting}
                            />
                        }
                        label="Active (schedule will run automatically)"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Batch Configuration
                    </Typography>
                </Grid>

                {/* Sites */}
                <Grid item xs={12}>
                    <SitesAutocomplete
                        disabled={isSubmitting}
                        required={false}
                    />
                </Grid>

                {/* Brands */}
                <Grid item xs={12}>
                    <BrandsAutocomplete
                        disabled={isSubmitting}
                    />
                </Grid>

                {/* Date Range - Only for raw2sku */}
                {batchType === 'raw2sku' && (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                required
                                label="Date Range"
                                helperText="Relative date range calculated at execution time"
                                value={scheduleFormData.value.config.date_range_type || 'last_30_days'}
                                onChange={e => scheduleFormData.value = {
                                    ...scheduleFormData.value,
                                    config: {
                                        ...scheduleFormData.value.config,
                                        date_range_type: e.target.value
                                    }
                                }}
                                disabled={isSubmitting}
                            >
                                {DATE_RANGE_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {scheduleFormData.value.config.date_range_type === 'custom' && (
                            <>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Date Start"
                                        value={createBatchFormData.value.dateStart || ''}
                                        onChange={e => {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateStart: e.target.value
                                            }
                                            scheduleFormData.value = {
                                                ...scheduleFormData.value,
                                                config: {
                                                    ...scheduleFormData.value.config,
                                                    dateStart: e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Date End"
                                        value={createBatchFormData.value.dateEnd || ''}
                                        onChange={e => {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateEnd: e.target.value
                                            }
                                            scheduleFormData.value = {
                                                ...scheduleFormData.value,
                                                config: {
                                                    ...scheduleFormData.value.config,
                                                    dateEnd: e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Filter by
                                </Typography>
                                <ToggleButtonGroup
                                    value={createBatchFormData.value.dateField || 'end_date'}
                                    exclusive
                                    onChange={(e, newValue) => {
                                        if (newValue !== null) {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateField: newValue
                                            }
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="small"
                                >
                                    <ToggleButton value="end_date">
                                        Last Seen Date
                                    </ToggleButton>
                                    <ToggleButton value="first_date">
                                        First Seen Date
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                    </>
                )}

                <ScheduleExecutionFields disabled={isSubmitting} />

                {/* Advanced: Auto-Validation Thresholds */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">
                                Advanced: Auto-Validation Thresholds
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Leave empty to use system defaults. Controls automatic validation and exclusion during batch processing.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Rate Auto-validate"
                                        placeholder="Default: 0.98"
                                        value={scheduleFormData.value.config.autoValidation?.minRateAutovalidate ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    minRateAutovalidate: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Minimum AI score to auto-validate (0–1). Default: 0.98">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Gap 2nd Candidate"
                                        placeholder="Default: 0.05"
                                        value={scheduleFormData.value.config.autoValidation?.minGapSecondCandidate ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    minGapSecondCandidate: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Min score gap between top and 2nd candidate. Default: 0.05">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Max Rate Auto-exclude"
                                        placeholder="Default: 0.80 (0 = disable)"
                                        value={scheduleFormData.value.config.autoValidation?.maxRateAutoexclude ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    maxRateAutoexclude: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Max score to auto-exclude. Set 0 to disable auto-exclusion. Default: 0.80">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </DrawerSeeker>
    )
}
